/**
 * Report Builder - Place Calculator JavaScript
 *
 * Manages calculator placement on the report canvas
 */

(function () {
  "use strict";

  // Store placed calculators
  let placedCalculators = [];
  let calculatorCounter = 0;
  let isAddingCalculatorPlaceholder = false;

  // Get calculator settings from template
  let calculatorSettings = {
    grams_per_cannula: 0,
    cost_per_cannula: 0,
    formula_a: 0,
    formula_b: 0,
    formula_c: 0,
    car_equivalent: 0,
    flight_equivalent: 0,
    car_reference: "",
    flight_reference: "",
    coffee_equivalent: 0,
    coffee_reference: "",
    carbon_calculator_description: "",
    cost_calculator_description: "",
  };

  // Load calculator settings from template
  function loadCalculatorSettings() {
    const settingsTemplate = document.getElementById("calculator-settings-data");
    if (settingsTemplate) {
      try {
        calculatorSettings = JSON.parse(settingsTemplate.textContent);
      } catch (e) {
        console.error("Failed to parse calculator settings:", e);
      }
    }
  }

  /**
   * Initialize the place calculator functionality
   */
  function initPlaceCalculator() {
    loadCalculatorSettings();
  }

  /**
   * Format number without unnecessary decimals
   */
  function formatNumber(num) {
    const fixed = num.toFixed(2);
    return parseFloat(fixed) === parseInt(fixed)
      ? parseInt(fixed).toString()
      : fixed;
  }

  /**
   * Format CO2 value with appropriate unit
   */
  function formatCO2(co2Saved) {
    let formattedResult;
    let unit;

    if (co2Saved >= 1000000) {
      formattedResult = formatNumber(co2Saved / 1000000);
      unit = "tonnes";
    } else if (co2Saved >= 1000) {
      formattedResult = formatNumber(co2Saved / 1000);
      unit = "kg";
    } else {
      formattedResult = formatNumber(co2Saved);
      unit = "grams";
    }

    return {
      value: parseFloat(formattedResult).toLocaleString(),
      unit: unit,
    };
  }

  /**
   * Format clinician equivalent value
   */
  function formatClinicianEquivalent(costSaved, formulaA, formulaB, formulaC) {
    if (formulaB === 0 || formulaC === 0) {
      return "";
    }
    const clinicianEquivalent = formulaA * (costSaved / formulaB) / formulaC;
    const formattedResult = formatNumber(clinicianEquivalent);
    return parseFloat(formattedResult).toLocaleString();
  }

  /**
   * Add calculator placeholder to canvas with inline form
   */
  function addCalculatorPlaceholder() {
    // Prevent duplicate calls
    if (isAddingCalculatorPlaceholder) {
      return;
    }

    isAddingCalculatorPlaceholder = true;

    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (!canvasCharts) {
      isAddingCalculatorPlaceholder = false;
      return;
    }

    // Hide empty message
    if (window.ReportBuilderUtils?.toggleEmptyMessage) {
      window.ReportBuilderUtils.toggleEmptyMessage(false);
    }

    // Create placeholder container
    const placeholderContainer = document.createElement("div");
    placeholderContainer.className = "canvas-calculator-placeholder";
    placeholderContainer.id = `calculator_placeholder_${Date.now()}`;

    // Clone the inline form template
    const inlineFormTemplate = document.querySelector(
      ".report-builder-place-calculator-inline"
    );
    if (!inlineFormTemplate) {
      console.error("Inline calculator form template not found");
      isAddingCalculatorPlaceholder = false;
      return;
    }

    const inlineForm = inlineFormTemplate.cloneNode(true);
    inlineForm.style.display = "block";
    inlineForm.id = `inline_calculator_form_${Date.now()}`;

    // Update IDs to be unique
    const selectType = inlineForm.querySelector("#inlineSelectCalculatorType");
    const cannulasInput = inlineForm.querySelector("#inlineCalculatorCannulas");
    const addCalculatorBtn = inlineForm.querySelector("#inlineAddCalculatorBtn");

    const uniqueId = Date.now();

    if (selectType) {
      selectType.id = `inlineSelectCalculatorType_${uniqueId}`;
    }
    if (cannulasInput) {
      cannulasInput.id = `inlineCalculatorCannulas_${uniqueId}`;
    }
    if (addCalculatorBtn) {
      addCalculatorBtn.id = `inlineAddCalculatorBtn_${uniqueId}`;
    }

    placeholderContainer.appendChild(inlineForm);
    canvasCharts.appendChild(placeholderContainer);

    // Initialize form handlers for this instance
    initInlineCalculatorFormInstance(inlineForm, placeholderContainer);

    // Scroll to the new placeholder
    placeholderContainer.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });

    // Reset flag after a short delay
    setTimeout(function () {
      isAddingCalculatorPlaceholder = false;
    }, 100);
  }

  /**
   * Initialize inline calculator form instance
   */
  function initInlineCalculatorFormInstance(
    formElement,
    placeholderContainer
  ) {
    const selectType = formElement.querySelector(
      "select[id^='inlineSelectCalculatorType']"
    );
    const cannulasInput = formElement.querySelector(
      "input[id^='inlineCalculatorCannulas']"
    );
    const addCalculatorBtn = formElement.querySelector(
      "button[id^='inlineAddCalculatorBtn']"
    );
    const cancelButtons = formElement.querySelectorAll(
      ".btn-cancel-calculator-config"
    );

    if (!selectType || !cannulasInput || !addCalculatorBtn) {
      return;
    }

    // Handle calculator type selection
    selectType.addEventListener("change", function () {
      const hasType = selectType.value !== "";
      cannulasInput.disabled = !hasType;
      if (!hasType) {
        cannulasInput.value = "";
      }
      updateAddCalculatorButtonState(
        selectType,
        cannulasInput,
        addCalculatorBtn
      );
    });

    // Handle cannulas input
    cannulasInput.addEventListener("input", function () {
      updateAddCalculatorButtonState(
        selectType,
        cannulasInput,
        addCalculatorBtn
      );
    });

    // Handle add calculator button
    addCalculatorBtn.addEventListener("click", function () {
      addCalculatorFromInlineFormInstance(formElement, placeholderContainer);
    });

    // Handle cancel buttons
    cancelButtons.forEach((btn) => {
      btn.addEventListener("click", function () {
        placeholderContainer.remove();

        // Show empty message if no items left
        const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
        if (canvasCharts && canvasCharts.children.length === 0) {
          if (window.ReportBuilderUtils?.toggleEmptyMessage) {
            window.ReportBuilderUtils.toggleEmptyMessage(true);
          }
        }
      });
    });
  }

  /**
   * Update add calculator button state
   */
  function updateAddCalculatorButtonState(
    selectType,
    cannulasInput,
    addCalculatorBtn
  ) {
    const hasType = selectType.value !== "";
    const hasCannulas =
      cannulasInput.value !== "" &&
      !isNaN(parseFloat(cannulasInput.value)) &&
      parseFloat(cannulasInput.value) >= 0;

    addCalculatorBtn.disabled = !(hasType && hasCannulas);
  }

  /**
   * Add calculator from inline form instance
   */
  function addCalculatorFromInlineFormInstance(
    formElement,
    placeholderContainer
  ) {
    const selectType = formElement.querySelector(
      "select[id^='inlineSelectCalculatorType']"
    );
    const cannulasInput = formElement.querySelector(
      "input[id^='inlineCalculatorCannulas']"
    );

    if (
      !selectType ||
      !cannulasInput ||
      !selectType.value ||
      !cannulasInput.value
    ) {
      return;
    }

    const calculatorType = selectType.value;
    const cannulaCount = parseFloat(cannulasInput.value);

    if (isNaN(cannulaCount) || cannulaCount < 0) {
      alert("Please enter a valid number of unused cannulas");
      return;
    }

    // Create unique calculator ID
    const calculatorItemId = `report_calculator_${++calculatorCounter}_${Date.now()}`;

    // Create calculator configuration
    const calculatorConfig = {
      id: calculatorItemId,
      type: calculatorType,
      cannulaCount: cannulaCount,
      customTitle: null, // Will default to calculator type if null
    };

    // Add to placed calculators
    placedCalculators.push(calculatorConfig);

    // Replace placeholder with calculator
    renderCalculatorInCanvas(calculatorConfig, placeholderContainer);
  }

  /**
   * Render calculator in canvas
   */
  function renderCalculatorInCanvas(calculatorConfig, placeholder = null) {
    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (!canvasCharts) {
      return;
    }

    // Create calculator container
    const calculatorContainer = document.createElement("div");
    calculatorContainer.className = "canvas-calculator-item";
    calculatorContainer.id = `calculator_container_${calculatorConfig.id}`;
    calculatorContainer.setAttribute("data-calculator-id", calculatorConfig.id);

    // Create calculator header with controls
    const calculatorHeader = document.createElement("div");
    calculatorHeader.className = "calculator-item-header";
    const displayTitle =
      calculatorConfig.customTitle ||
      (calculatorConfig.type === "cost"
        ? "Cost Calculator"
        : "Carbon Calculator");

    if (window.ReportBuilderUtils?.generateItemHeaderHtml) {
      calculatorHeader.innerHTML =
        window.ReportBuilderUtils.generateItemHeaderHtml({
          itemId: calculatorConfig.id,
          displayTitle: displayTitle,
          idAttribute: "data-calculator-id",
          itemType: "calculator",
          removeButtonClass: "btn-remove-calculator",
          removeButtonTitle: "Remove calculator",
        });
    } else {
      // Fallback if utils not available
      calculatorHeader.innerHTML = `<div class="calculator-item-title"><h4>${displayTitle}</h4></div>`;
    }

    // Create calculator content area
    const calculatorContent = document.createElement("div");
    calculatorContent.className = "calculator-item-content";
    calculatorContent.id = `calculator_content_${calculatorConfig.id}`;

    // Generate calculator HTML based on type
    const calculatorHtml = generateCalculatorHtml(
      calculatorConfig,
      calculatorContent.id
    );
    calculatorContent.innerHTML = calculatorHtml;
    setupDescriptionEditing(calculatorContent, calculatorConfig);

    // Assemble calculator container
    calculatorContainer.appendChild(calculatorHeader);
    calculatorContainer.appendChild(calculatorContent);

    // Replace placeholder or add to canvas
    if (placeholder && placeholder.parentNode) {
      placeholder.parentNode.replaceChild(calculatorContainer, placeholder);
    } else {
      canvasCharts.appendChild(calculatorContainer);
    }

    // Setup move buttons
    if (window.ReportBuilderUtils?.setupMoveButtons) {
      window.ReportBuilderUtils.setupMoveButtons(
        calculatorContainer,
        calculatorConfig.id,
        "data-calculator-id",
        function () {
          updateCalculatorArrayOrder(calculatorConfig.id);
        }
      );
    }

    // Setup edit title button
    if (window.ReportBuilderUtils?.setupEditTitleButton) {
      window.ReportBuilderUtils.setupEditTitleButton(
        calculatorContainer,
        calculatorConfig.id,
        "calculator",
        function (newTitle) {
          calculatorConfig.customTitle = newTitle;
        }
      );
    }

    // Setup remove button
    const removeBtn = calculatorContainer.querySelector(
      ".btn-remove-calculator"
    );
    if (removeBtn) {
      removeBtn.addEventListener("click", function () {
        if (window.ReportBuilderUtils?.showConfirmationModal) {
          window.ReportBuilderUtils.showConfirmationModal({
            title: "Remove Calculator",
            message: "Are you sure you want to remove this calculator? This action cannot be undone.",
            confirmText: "Remove",
            cancelText: "Cancel",
            onConfirm: function() {
              removeCalculator(calculatorConfig.id);
            }
          });
        } else {
          // Fallback if modal not available
          if (confirm("Are you sure you want to remove this calculator?")) {
            removeCalculator(calculatorConfig.id);
          }
        }
      });
    }
  }

  /**
   * The CMS description for a calculator type
   */
  function defaultDescription(isCost) {
    return (
      (isCost
        ? calculatorSettings.cost_calculator_description
        : calculatorSettings.carbon_calculator_description) || ""
    ).trim();
  }

  /**
   * Click-to-edit for the calculator description. Plain text only: Enter
   * adds a line break, blur saves, Escape cancels. An emptied description
   * shows a placeholder on the canvas and is left out of the PDF.
   */
  function setupDescriptionEditing(container, calculatorConfig) {
    const element = container.querySelector(".rb-calc-description");
    if (!element) {
      return;
    }

    let contentBeforeEdit = "";

    element.addEventListener("click", function () {
      if (element.isContentEditable) {
        return;
      }
      contentBeforeEdit = element.textContent;
      element.classList.remove("is-empty");
      element.contentEditable = "true";
      element.focus();
    });

    // Keep pasted content as plain text
    element.addEventListener("paste", function (e) {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData("text");
      document.execCommand("insertText", false, text);
    });

    element.addEventListener("keydown", function (e) {
      if (!element.isContentEditable) {
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        element.textContent = contentBeforeEdit;
        element.blur();
      } else if (e.key === "Enter") {
        // A plain newline (shown via white-space: pre-line) rather than the
        // <div>/<br> the browser would insert
        e.preventDefault();
        document.execCommand("insertText", false, "\n");
      }
    });

    element.addEventListener("blur", function () {
      if (!element.isContentEditable) {
        return;
      }
      element.contentEditable = "false";
      const text = element.innerText.replace(/\n{3,}/g, "\n\n").trim();
      element.textContent = text;
      element.classList.toggle("is-empty", text === "");
      calculatorConfig.description = text;
    });
  }

  /**
   * Generate calculator HTML
   */
  function generateCalculatorHtml(calculatorConfig, containerId) {
    const escapeHtml = window.ReportBuilderUtils?.escapeHtml || function(text) {
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    };

    const isCost = calculatorConfig.type === "cost";

    // Calculate initial results
    const cannulaCount = calculatorConfig.cannulaCount;
    const gramsPerCannula = calculatorSettings.grams_per_cannula || 0;
    const costPerCannula = calculatorSettings.cost_per_cannula || 0;
    const formulaA = calculatorSettings.formula_a || 0;
    const formulaB = calculatorSettings.formula_b || 0;
    const formulaC = calculatorSettings.formula_c || 0;
    const carEquivalent = calculatorSettings.car_equivalent || 0;
    const flightEquivalent = calculatorSettings.flight_equivalent || 0;
    const carReference = (calculatorSettings.car_reference || "").trim();
    const flightReference = (calculatorSettings.flight_reference || "").trim();
    const coffeeEquivalent = calculatorSettings.coffee_equivalent || 0;
    const coffeeReference = (calculatorSettings.coffee_reference || "").trim();

    // Explanatory copy. Defaults to Site Settings > Calculator Settings (the
    // same text as the main site's widgets) and can be edited per report.
    const description =
      typeof calculatorConfig.description === "string"
        ? calculatorConfig.description
        : defaultDescription(isCost);

    // One labelled figure. `valueHtml` is trusted markup built here.
    // `referenceNumber` adds a superscript pointing at a numbered reference.
    function stat(label, valueHtml, modifier, referenceNumber) {
      const referenceMark = referenceNumber
        ? `<sup class="rb-calc-reference-mark">${referenceNumber}</sup>`
        : "";
      return `
        <div class="rb-calc-stat${modifier ? " rb-calc-stat--" + modifier : ""}">
          <span class="rb-calc-label">${escapeHtml(label)}${referenceMark}</span>
          <span class="rb-calc-value">${valueHtml}</span>
        </div>
      `;
    }

    // Cost calculator reference HTML from Site Settings (already numbered)
    const references = [];
    // Carbon references: one numbered list, with matching superscripts on the
    // results. Numbered in order added, so hidden results leave no gap.
    const numberedReferences = [];
    const resultStats = [];
    const referenceHelpers = window.MchriCalculatorReferences || {
      stripReferenceHeading: function (html) {
        return html;
      },
      hasReferenceContent: function (html) {
        return !!html;
      },
    };

    // Add a carbon reference and return its number (null when there isn't one)
    function addNumberedReference(html) {
      const content = referenceHelpers.stripReferenceHeading(html).trim();
      if (!referenceHelpers.hasReferenceContent(content)) return null;
      numberedReferences.push(content);
      return numberedReferences.length;
    }

    if (isCost) {
      const costSaved = cannulaCount * costPerCannula;
      const costValue = parseFloat(formatNumber(costSaved)).toLocaleString();
      resultStats.push(
        stat(
          "Estimated Cost Savings",
          `<span class="rb-calc-unit">AU$</span> <span class="cost-calculator-result-value">${escapeHtml(costValue)}</span>`,
          "primary"
        )
      );

      if (formulaB !== 0 && formulaC !== 0) {
        const clinicianEquivalent = formatClinicianEquivalent(
          costSaved,
          formulaA,
          formulaB,
          formulaC
        );
        resultStats.push(
          stat(
            "The equivalent number of clinician workdays represented by the cost of unused cannulas for this audit",
            `<span class="cost-calculator-result-clinician-value">${escapeHtml(clinicianEquivalent)}</span> <span class="rb-calc-unit">workdays</span>`
          )
        );
      }

      const costReferenceText =
        (calculatorSettings.cost_calculator_reference_text || "").trim();
      if (costReferenceText) references.push(costReferenceText);
    } else {
      const totalEmissions = cannulaCount * gramsPerCannula;
      const co2Saved = totalEmissions * 0.1;
      const co2SavedValue = parseFloat(formatNumber(co2Saved)).toLocaleString();
      const totalEmissionsValue = parseFloat(
        formatNumber(totalEmissions)
      ).toLocaleString();
      resultStats.push(
        stat(
          "Total emissions of unused cannulas",
          `<span data-result="total">${escapeHtml(totalEmissionsValue)}</span> <span class="rb-calc-unit">gCO₂e</span>`
        ),
        stat(
          "Estimated CO₂ emissions savings from a 10% reduction in unused cannulas",
          `<span class="carbon-calculator-result-value">${escapeHtml(co2SavedValue)}</span> <span class="rb-calc-unit">gCO₂e</span>`,
          "primary"
        )
      );

      if (carEquivalent > 0) {
        const equivalentCarMeters = parseFloat(
          formatNumber(totalEmissions * carEquivalent)
        ).toLocaleString();
        resultStats.push(
          stat(
            "Car travel equivalent (total emissions)",
            `<span data-equivalent="car">${escapeHtml(equivalentCarMeters)} m</span>`,
            null,
            addNumberedReference(carReference)
          )
        );
      }

      if (flightEquivalent > 0) {
        const equivalentFlightMeters = parseFloat(
          formatNumber(totalEmissions * flightEquivalent)
        ).toLocaleString();
        resultStats.push(
          stat(
            "Flight travel equivalent (total emissions)",
            `<span data-equivalent="flight">${escapeHtml(equivalentFlightMeters)} m</span>`,
            null,
            addNumberedReference(flightReference)
          )
        );
      }

      // Coffee cups avoided = carbon saved ÷ gCO₂e per disposable cup (same
      // as the main site's carbon widget)
      if (coffeeEquivalent > 0) {
        const equivalentCoffeeCups = parseFloat(
          formatNumber(co2Saved / coffeeEquivalent)
        ).toLocaleString();
        resultStats.push(
          stat(
            "Disposable coffee cup equivalent (emissions saved)",
            `<span data-equivalent="coffee">${escapeHtml(equivalentCoffeeCups)} cups</span>`,
            null,
            addNumberedReference(coffeeReference)
          )
        );
      }
    }

    // Icons from the main site's calculator widgets: $ for cost, a drop for
    // carbon, and a tick on the results panel
    const typeIcon = isCost
      ? '<line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />'
      : '<path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />';
    const svg = (paths) =>
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`;

    // Report-builder layout (not the main site's widget): description and
    // input on the left, results panel on the right, references underneath. Sized
    // to match the other canvas items, and captured as-is for the PDF.
    return `
      <div class="rb-calc rb-calc--${isCost ? "cost" : "carbon"}">
        <div class="rb-calc-columns">
          <div class="rb-calc-column">
            <div class="rb-calc-icon" aria-hidden="true">${svg(typeIcon)}</div>
            <p class="rb-calc-description${description ? "" : " is-empty"}" title="Click to edit" data-placeholder="Add a description">${escapeHtml(description)}</p>
            ${stat("Number of Unused Cannulas", escapeHtml(Number(cannulaCount).toLocaleString()))}
          </div>
          <div class="rb-calc-column rb-calc-results">
            <div class="rb-calc-result-icon" aria-hidden="true">${svg('<polyline points="20 6 9 17 4 12" />')}</div>
            ${resultStats.join("")}
          </div>
        </div>
        ${
          references.length
            ? `<div class="rb-calc-references">${references
                .map((html) => `<div class="rb-calc-reference">${html}</div>`)
                .join("")}</div>`
            : ""
        }
        ${
          numberedReferences.length
            ? `<div class="rb-calc-references">
                <p class="rb-calc-references-title">References:</p>
                ${numberedReferences
                  .map(
                    (html, index) =>
                      `<div class="rb-calc-reference rb-calc-reference--numbered"><span class="rb-calc-reference-number">${index + 1}.</span><div class="rb-calc-reference-body">${html}</div></div>`
                  )
                  .join("")}
              </div>`
            : ""
        }
      </div>
    `;
  }

  /**
   * Update calculator array order to match DOM order
   */
  function updateCalculatorArrayOrder(calculatorId) {
    if (window.ReportBuilderUtils?.updateArrayOrder) {
      window.ReportBuilderUtils.updateArrayOrder(
        placedCalculators,
        calculatorId,
        "calculator_container_",
        "data-calculator-id"
      );
    }
  }

  /**
   * Remove calculator from canvas
   */
  function removeCalculator(calculatorId) {
    // Remove from placed calculators array
    placedCalculators = placedCalculators.filter(
      (c) => c.id !== calculatorId
    );

    // Remove from DOM
    const calculatorContainer = document.getElementById(
      `calculator_container_${calculatorId}`
    );
    if (calculatorContainer) {
      calculatorContainer.remove();
    }

    // Show empty message if no items left
    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (canvasCharts && canvasCharts.children.length === 0) {
      if (window.ReportBuilderUtils?.toggleEmptyMessage) {
        window.ReportBuilderUtils.toggleEmptyMessage(true);
      }
    }
  }

  // Export functions for external use
  window.ReportBuilderCalculator = {
    addCalculatorPlaceholder: addCalculatorPlaceholder,
    removeCalculator: removeCalculator,
  };

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initPlaceCalculator);
  } else {
    initPlaceCalculator();
  }
})();
